import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  inject,
} from '@angular/core';
import { FilterDayEnum, PAGE_ICONS } from 'src/app/global-constants';
import { RAGApiService } from 'src/app/services/rag-api/rag-api.service';
import { filterBatchInformation } from '../processedfiles/filterable-batch';
import {
  AdminSettingsDto,
  BatchDto,
  BatchRecordUpdateType,
  Status,
} from '@wex-risk/rp-portfolio-utils';
import { DataTableColumn } from 'src/app/components/data-table/data-table.component';

/** Maximum number of failed-record rows surfaced in the "Last files with failures" table. */
const MAX_ATTENTION_ROWS = 5;

export interface AttentionRow {
  id: string;
  fileName: string;
  submittedOn: Date;
  failedRecords: number;
}

@Component({
  selector: 'app-home',
  templateUrl: './home.component.html',
  styleUrls: ['./home.component.scss'],
  standalone: false,
})
// Home page component
export class HomeComponent implements AfterViewInit {
  protected readonly pageIcon = PAGE_ICONS.home;
  private ragService = inject(RAGApiService);
  private cdr = inject(ChangeDetectorRef);

  protected cliBatchFiles: BatchDto[] = [];
  protected cldBatchFiles: BatchDto[] = [];
  protected cliSuccesses = 0;
  protected cliFailures = 0;
  protected cldSuccesses = 0;
  protected cldFailures = 0;
  protected adminSettings: AdminSettingsDto | null = null;
  protected attentionRows: AttentionRow[] = [];
  protected filesLoading = true;
  protected filesError = false;

  protected readonly attentionColumns: DataTableColumn[] = [
    { key: 'fileName', label: 'File name', width: '3fr' },
    { key: 'submittedOn', label: 'Submitted', width: '1.4fr' },
    { key: 'failedRecords', label: 'Failed records', width: '1.4fr' },
  ];

  // retrieves files after view is initialized
  ngAfterViewInit(): void {
    this.ragService.adminSettings$.subscribe(
      (settings) => {
        this.adminSettings = settings;
        this.cdr.detectChanges(); // Manually trigger change detection
      },
      (err) => console.error(err),
    );
    void this.getFiles();
  }

  // retrieves files
  async getFiles(): Promise<void> {
    this.filesLoading = true;
    this.filesError = false;

    try {
      const files = await this.ragService.getFiles();

      this.cldBatchFiles = filterBatchInformation(files.files, {
        updateTypes: BatchRecordUpdateType.CLD,
        submittedOn: FilterDayEnum.Days30,
        status: [Status.ACCEPTED, Status.FAILED],
      });

      this.cliBatchFiles = filterBatchInformation(files.files, {
        updateTypes: BatchRecordUpdateType.CLI,
        submittedOn: FilterDayEnum.Days30,
        status: [Status.ACCEPTED, Status.FAILED],
      });

      this.cliFailures = this.cliBatchFiles.filter(
        (r) => r.failedRecords > 0,
      ).length;
      this.cliSuccesses = this.cliBatchFiles.filter(
        (r) => r.successRecords === r.totalRecords,
      ).length;
      this.cldFailures = this.cldBatchFiles.filter(
        (r) => r.failedRecords > 0,
      ).length;
      this.cldSuccesses = this.cldBatchFiles.filter(
        (r) => r.successRecords === r.totalRecords,
      ).length;

      this.attentionRows = this.buildAttentionRows();
    } catch (error: unknown) {
      this.filesError = true;
      console.error(error);
    } finally {
      this.filesLoading = false;
      this.cdr.detectChanges(); // Manually trigger change detection
    }
  }

  /**
   * Builds the "Last files with failures" table rows from the batch-file data already
   * fetched above (no new endpoints): every CLI/CLD file with failed
   * records, most recently submitted first, capped to a scannable count.
   */
  private buildAttentionRows(): AttentionRow[] {
    return [...this.cliBatchFiles, ...this.cldBatchFiles]
      .filter((file) => file.failedRecords > 0)
      .sort(
        (a, b) =>
          new Date(b.submittedOn).getTime() - new Date(a.submittedOn).getTime(),
      )
      .slice(0, MAX_ATTENTION_ROWS)
      .map((file) => ({
        id: file.id,
        fileName: file.fileName,
        submittedOn: file.submittedOn,
        failedRecords: file.failedRecords,
      }));
  }

  protected readonly trackByAttentionRow = (
    _index: number,
    row: unknown,
  ): string => (row as AttentionRow).id;
}
